import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import { getDaikaiEvent, upsertJDaikaiParticipantsFromUnickName } from "../../api/project";
import {
    ApplyWrap,
    ApplyTitle,
    ApplyCard,
    ApplyBody,
    ApplyEventInfo,
    ApplyImage,
    ApplyField,
    ApplyLabel,
    ApplyInput,
    ApplyTextArea,
    ApplyButton,
    ApplyMutedText,
} from "./DaikaiApply.style";

// 大会申込画面のデフォルト申込項目定義(大会情報に申込項目JSONが登録されていない場合のフォールバック)
const DEFAULT_FORM_ITEMS = [
    {
        name: "wantName",
        label: "大会の際の名前（＊必須）：",
        isShow: "1",
        mustFlg: "1",
        hintText: "名前",
    },
    {
        name: "email",
        label: "メールアドレス（＊必須）：",
        isShow: "1",
        mustFlg: "1",
        hintText: "",
    },
    {
        name: "tel",
        label: "電話番号（＊必須）：",
        isShow: "1",
        mustFlg: "1",
        hintText: "",
    },
    {
        name: "sameTablePro",
        label: "同卓希望プロ：\n※ご希望に添えない場合がございます",
        isShow: "1",
        mustFlg: "0",
        hintText: "村上淳プロ／坂井秀隆プロ／安藤りなプロ",
    },
    {
        name: "daikaiMemo",
        label: "メモ（任意）：",
        isShow: "1",
        mustFlg: "0",
        hintText: "",
        maxLines: 3,
        maxLength: 100,
    },
];

const parseFormItems = (jsonText) => {
    try {
        const parsed = JSON.parse(jsonText);
        if (Array.isArray(parsed)) {
            return parsed;
        }
    } catch (e) {
        // JSON不正の場合はデフォルト値を使用する
    }
    return DEFAULT_FORM_ITEMS;
};

function DaikaiApply() {
    const { daikaiId } = useParams();

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formItems, setFormItems] = useState(DEFAULT_FORM_ITEMS);
    const [applicantName, setApplicantName] = useState("");
    const [values, setValues] = useState({});

    useEffect(() => {
        const loadEvent = async () => {
            setLoading(true);
            try {
                const res = await getDaikaiEvent(daikaiId);
                const ev = res?.data;
                setEvent(ev);
                const items = parseFormItems(ev?.input_item_json);
                setFormItems(items);
                const init = {};
                items
                    .filter((item) => item.isShow === "1")
                    .forEach((item) => {
                        init[item.name] = "";
                    });
                setValues(init);
                setApplicantName(sessionStorage.getItem("userName") || "");
            } catch (error) {
                console.error("Failed to load daikai event:", error);
                toast.error("大会情報の取得に失敗しました");
            } finally {
                setLoading(false);
            }
        };
        if (daikaiId) {
            loadEvent();
        }
    }, [daikaiId]);

    if (loading) {
        return (
            <MainLayout>
                <ApplyWrap>
                    <ApplyBody
                        style={{
                            textAlign: "center",
                            padding: "60px 0",
                            fontSize: "16px",
                        }}
                    >
                        読み込み中...
                    </ApplyBody>
                </ApplyWrap>
            </MainLayout>
        );
    }

    const shownItems = formItems.filter((item) => item.isShow === "1");
    const requiredItems = shownItems.filter((item) => item.mustFlg === "1");

    const handleChange = (name, value) => {
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        // 必須項目チェック
        for (const item of requiredItems) {
            const v = (values[item.name] || "").toString().trim();
            if (!v) {
                toast.error(`「${item.label}」は必須です`);
                return;
            }
        }
        if (!applicantName || !applicantName.toString().trim()) {
            toast.error("申込者名は必須です");
            return;
        }

        const formData = {};
        shownItems.forEach((item) => {
            formData[item.name] = (values[item.name] || "").toString();
        });

        const payload = {
            userId: sessionStorage.getItem("userId") || "web_" + Date.now(),
            userName: applicantName,
            email: formData.email || "",
            wantSameTableProName: formData.sameTablePro || formData.wantSameTableProName || "",
            daikaiId: String(event?.id ?? daikaiId ?? ""),
            daikaiTitle: `${event?.daikai_title1 ?? ""} ${event?.daikai_title2 ?? ""}<${event?.date_time ?? ""} ${event?.location ?? ""}>`,
            daikaiMemo: formData.daikaiMemo || "",
            otherJsonItem: JSON.stringify(formData),
        };

        setSaving(true);
        try {
            const res = await upsertJDaikaiParticipantsFromUnickName(payload);
            if (res?.code === 200) {
                setSubmitted(true);
                toast.success("申込完了しました");
            } else {
                toast.error(res?.msg || "申込に失敗しました");
            }
        } catch (error) {
            console.error("Daikai apply failed:", error);
            toast.error("申込に失敗しました: " + (error?.message || ""));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setValues({});
        setApplicantName(sessionStorage.getItem("userName") || "");
        setSubmitted(false);
    };

    return (
        <MainLayout>
            <ApplyWrap>
                <ApplyTitle>大会申込</ApplyTitle>
                <ApplyCard>
                    <ApplyBody>
                        {event?.image_url && (
                            <ApplyImage>
                                <img
                                    src={event.image_url}
                                    alt="大会画像"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                    }}
                                />
                            </ApplyImage>
                        )}

                        {event && (
                            <ApplyEventInfo>
                                {`${event.daikai_title1 ?? ""} ${event.daikai_title2 ?? ""}\n${event.date_time ?? ""} ${event.location ?? ""}`}
                            </ApplyEventInfo>
                        )}

                        {submitted ? (
                            <ApplyBody>
                                <ApplyMutedText>申込が完了しました。</ApplyMutedText>
                                <ApplyButton onClick={handleReset}>申込画面に戻る</ApplyButton>
                            </ApplyBody>
                        ) : (
                            <>
                                <ApplyField>
                                    <ApplyLabel>申込者名（＊必須）</ApplyLabel>
                                    <ApplyInput
                                        value={applicantName}
                                        onChange={(e) => setApplicantName(e.target.value)}
                                        placeholder="雀活マスター利用名"
                                    />
                                </ApplyField>

                                {shownItems.map((item) => {
                                    const name = item.name;
                                    const value = values[name] ?? "";
                                    const inputType =
                                        name === "email" ? "email" : name === "tel" ? "tel" : "text";
                                    const isTextArea = Number(item.maxLines) > 1 || name === "daikaiMemo";
                                    return (
                                        <ApplyField key={name}>
                                            <ApplyLabel>{item.label}</ApplyLabel>
                                            {item.hintText ? <ApplyMutedText>{item.hintText}</ApplyMutedText> : null}
                                            {isTextArea ? (
                                                <ApplyTextArea
                                                    value={value}
                                                    onChange={(e) => handleChange(name, e.target.value)}
                                                    maxLength={item.maxLength ? Number(item.maxLength) : undefined}
                                                />
                                            ) : (
                                                <ApplyInput
                                                    type={inputType}
                                                    value={value}
                                                    onChange={(e) => handleChange(name, e.target.value)}
                                                    maxLength={item.maxLength ? Number(item.maxLength) : undefined}
                                                />
                                            )}
                                        </ApplyField>
                                    );
                                })}

                                <ApplyButton onClick={handleSubmit} disabled={saving}>
                                    {saving ? "送信中..." : "参加します"}
                                </ApplyButton>
                            </>
                        )}
                    </ApplyBody>
                </ApplyCard>
            </ApplyWrap>
        </MainLayout>
    );
}

export default DaikaiApply;
